import * as XLSX from 'xlsx';

/** 标准 GL Category Excel 行；parentCode 用于解析 parentId */
export type GlCategoryExcelRow = {
    code: string;
    nameEn?: string;
    nameZh?: string;
    type?: string;
    parentCode?: string;
    level?: number;
    fsCode?: string;
    ppeCategory?: string;
    isActive?: number;
};

const HEADERS = [
    'code',
    'nameEn',
    'nameZh',
    'type',
    'parentCode',
    'level',
    'fsCode',
    'ppeCategory',
    'isActive',
] as const;

const VALID_TYPES = new Set([
    'ASSET',
    'LIABILITY',
    'EQUITY',
    'REVENUE',
    'EXPENSE',
    'ADJUSTMENT',
]);

function cellStr(v: unknown): string {
    if (v == null) return '';
    return String(v).trim();
}

function cellNum(v: unknown): number | undefined {
    if (v == null || cellStr(v) === '') return undefined;
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
}

export function buildGlCategoryExportFileName(): string {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `gl-category-${y}${m}${day}.xlsx`;
}

type ExportCat = {
    code?: string;
    nameEn?: string;
    nameZh?: string;
    type?: string;
    parentId?: number | null;
    level?: number;
    fsCode?: string;
    ppeCategory?: string;
    isActive?: number;
    categoryId?: number;
};

/**
 * 按页面树序导出：根按 code 排序，同层兄弟按 code，深度优先
 * （A → A-CA → A-CA-AR … → L → L-xx …）。
 */
export function orderCategoriesLikePageTree(rows: ExportCat[]): ExportCat[] {
    type Node = ExportCat & {children: Node[]};
    const byId = new Map<number, Node>();
    for (const r of rows) {
        if (r.categoryId == null || !r.code) continue;
        byId.set(r.categoryId, {...r, children: []});
    }
    const roots: Node[] = [];
    for (const node of byId.values()) {
        const pid = node.parentId;
        if (pid != null && byId.has(pid)) {
            byId.get(pid)!.children.push(node);
        } else {
            roots.push(node);
        }
    }
    const byCode = (a: Node, b: Node) => (a.code || '').localeCompare(b.code || '', undefined, {sensitivity: 'base'});
    const sortRec = (list: Node[]) => {
        list.sort(byCode);
        for (const n of list) sortRec(n.children);
    };
    sortRec(roots);

    const flat: ExportCat[] = [];
    const walk = (list: Node[]) => {
        for (const n of list) {
            const {children, ...rest} = n;
            flat.push(rest);
            walk(children);
        }
    };
    walk(roots);
    return flat;
}

/**
 * 导出全量标准科目（扁平表）。parentCode 由 parentId → code 映射写入。
 * 行序与页面树一致（DFS + code 排序）。
 */
export function exportGlCategoriesToXlsx(rows: ExportCat[], fileName: string): void {
    const ordered = orderCategoriesLikePageTree(rows);
    const idToCode = new Map<number, string>();
    for (const r of ordered) {
        if (r.categoryId != null && r.code) idToCode.set(r.categoryId, r.code);
    }

    const data = ordered.map((r) => ({
        code: (r.code || '').trim(),
        nameEn: (r.nameEn || '').trim(),
        nameZh: (r.nameZh || '').trim(),
        type: (r.type || '').trim(),
        parentCode: r.parentId != null ? (idToCode.get(r.parentId) || '') : '',
        level: r.level ?? '',
        fsCode: (r.fsCode || '').trim(),
        ppeCategory: (r.ppeCategory || '').trim(),
        isActive: r.isActive ?? 1,
    }));

    const sheet = XLSX.utils.json_to_sheet(data, {header: [...HEADERS]});
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, 'GLCategory');
    XLSX.writeFile(book, fileName);
}

/**
 * 解析导入文件。同 code 后者覆盖。按 level 升序返回便于先建父再建模。
 */
export async function parseGlCategoriesFromXlsx(file: File): Promise<{
    rows: GlCategoryExcelRow[];
    skippedEmpty: number;
}> {
    const buf = await file.arrayBuffer();
    const book = XLSX.read(buf, {type: 'array'});
    const sheetName = book.SheetNames[0];
    if (!sheetName) return {rows: [], skippedEmpty: 0};

    const sheet = book.Sheets[sheetName];
    const raw = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, {defval: ''});

    const byCode = new Map<string, GlCategoryExcelRow>();
    let skippedEmpty = 0;

    for (const line of raw) {
        const get = (...keys: string[]) => {
            for (const k of keys) {
                if (line[k] != null && cellStr(line[k]) !== '') return cellStr(line[k]);
                const found = Object.keys(line).find((h) => h.trim().toLowerCase() === k.toLowerCase());
                if (found) return cellStr(line[found]);
            }
            return '';
        };
        const getRaw = (...keys: string[]) => {
            for (const k of keys) {
                if (line[k] != null && cellStr(line[k]) !== '') return line[k];
                const found = Object.keys(line).find((h) => h.trim().toLowerCase() === k.toLowerCase());
                if (found && cellStr(line[found]) !== '') return line[found];
            }
            return undefined;
        };

        const code = get('code', 'GL Code', 'glCode');
        if (!code) {
            skippedEmpty += 1;
            continue;
        }

        const typeRaw = get('type').toUpperCase();
        const type = typeRaw && VALID_TYPES.has(typeRaw) ? typeRaw : undefined;
        const level = cellNum(getRaw('level'));
        const isActiveRaw = cellNum(getRaw('isActive', 'is_active', 'active'));

        byCode.set(code, {
            code,
            nameEn: get('nameEn', 'Name (EN)', 'name_en') || undefined,
            nameZh: get('nameZh', 'Name (ZH)', 'name_zh') || undefined,
            type,
            parentCode: get('parentCode', 'parent_code', 'Parent Code') || undefined,
            level,
            fsCode: get('fsCode', 'fs_code', 'FS Code') || undefined,
            ppeCategory: get('ppeCategory', 'ppe_category') || undefined,
            isActive: isActiveRaw ?? 1,
        });
    }

    const rows = [...byCode.values()].sort((a, b) => {
        const la = a.level ?? 99;
        const lb = b.level ?? 99;
        if (la !== lb) return la - lb;
        return a.code.localeCompare(b.code);
    });

    return {rows, skippedEmpty};
}
